const GEMINI_API_BASE = 'https://generativelanguage.googleapis.com/v1beta/models';

export const DEFAULT_GEMINI_MODEL = 'gemini-3.5-flash';

export const GEMINI_FREE_MODELS = [
  { value: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash' },
  { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite' },
  { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' }
];

// Ієрархія моделей для retry (на основі GEMINI_FREE_MODELS)
const MODEL_HIERARCHY = GEMINI_FREE_MODELS.map(m => m.value);

// Максимум спроб на одну модель (1 = 2 спроби)
const MAX_RETRIES_PER_MODEL = 1;

// Таймаут одного запиту до моделі
const REQUEST_TIMEOUT_MS = 60_000; // 1 хвилина

// Час скидання деградації моделі
const DEMOTION_RESET_MS = 60 * 60_000; // 1 година

// Стан деградації моделі між викликами (якщо модель двічі відмовила —
// наступний чек одразу починає з наступної по ієрархії)
let _demotedModelIndex = 0;
let _demotionTimestamp: number | null = null;

const _demoteModel = (toIndex: number) => {
  if (toIndex > _demotedModelIndex && toIndex < MODEL_HIERARCHY.length) {
    _demotedModelIndex = toIndex;
    _demotionTimestamp = Date.now();
    console.warn(`[Gemini] Деградація: наступний запит починатиметься з моделі "${MODEL_HIERARCHY[toIndex]}"`);
  }
};

const _checkDemotionReset = () => {
  if (_demotionTimestamp !== null && Date.now() - _demotionTimestamp >= DEMOTION_RESET_MS) {
    console.info('[Gemini] Скидання деградації — повертаємось до основної моделі');
    _demotedModelIndex = 0;
    _demotionTimestamp = null;
  }
};

const getGeminiApiUrl = (model: string) =>
  `${GEMINI_API_BASE}/${model}:generateContent`;

// Функція для отримання API ключа (буде викликатися з компонента)
let cachedApiKey: string | null = null;
let apiKeyPromise: Promise<string | null> | null = null;

export const getGeminiApiKey = async (): Promise<string | null> => {
  if (cachedApiKey) {
    return cachedApiKey;
  }

  if (apiKeyPromise) {
    return apiKeyPromise;
  }

  apiKeyPromise = (async () => {
    try {
      const { geminiSettingsService } = await import('./geminiSettingsService');
      const key = await geminiSettingsService.getApiKey();
      if (key) {
        cachedApiKey = key;
      }
      return key;
    } catch (error) {
      console.error('Помилка завантаження API ключа:', error);
      return null;
    } finally {
      apiKeyPromise = null;
    }
  })();

  return apiKeyPromise;
};

export const clearCachedApiKey = () => {
  cachedApiKey = null;
  apiKeyPromise = null;
};

export interface ExtractedItem {
  name: string;
  price: number;
  categoryId?: string;
}

export interface ExtractedReceipt {
  receiptName?: string;
  receiptDate?: string; // YYYY-MM-DD
  accountId?: string;
  items: ExtractedItem[];
}

export interface GeminiExtractResponse {
  receipts: ExtractedReceipt[];
  retryInfo?: {
    modelUsed: string;
    wasRetry: boolean;
    originalModel?: string;
    failureReason?: string;
  };
}

export interface CategoryInfo {
  id: string;
  name: string;
}

export interface AccountInfo {
  id: string;
  name: string;
}

export interface ReceiptImageInput {
  base64Data: string;
  mimeType: string;
}

export const geminiService = {
  /**
   * Обробляє текст чеку через GEMINI API та витягує назви товарів та їх ціни
   * з автоматичним retry на резервній моделі при помилці
   * @param receiptText - текст чека для обробки
   * @param apiKey - API ключ Gemini (опціонально)
   * @param categories - список доступних категорій для автоматичного призначення
   */
  async extractItemsFromReceipt(
    receiptText: string, 
    apiKey?: string,
    categories?: CategoryInfo[],
    receiptImage?: ReceiptImageInput,
    model?: string,
    receiptImages?: ReceiptImageInput[],
    accounts?: AccountInfo[]
  ): Promise<GeminiExtractResponse> {
    // Перевіряємо чи пора скидати деградацію (минула 1 година)
    _checkDemotionReset();

    const selectedModel = model || DEFAULT_GEMINI_MODEL;
    const requestedIndex = MODEL_HIERARCHY.indexOf(selectedModel);

    if (requestedIndex === -1) {
      throw new Error(`Модель "${selectedModel}" не знайдена в ієрархії`);
    }

    // Якщо поточна модель деградована, стартуємо з вищого індексу
    const effectiveStartIndex = Math.max(requestedIndex, _demotedModelIndex);

    let lastError: Error | null = null;
    let lastFailureReason: string | null = null;

    // Спробуємо моделі, починаючи з ефективного індексу
    for (let modelIdx = effectiveStartIndex; modelIdx < MODEL_HIERARCHY.length; modelIdx++) {
      const currentModel = MODEL_HIERARCHY[modelIdx];
      const isRetry = modelIdx > requestedIndex;

      // На кожну модель даємо MAX_RETRIES_PER_MODEL + 1 спроб (тобто 2 спроби)
      for (let attempt = 0; attempt <= MAX_RETRIES_PER_MODEL; attempt++) {
        try {
          const result = await this._extractWithModel(
            receiptText,
            apiKey,
            categories,
            receiptImage,
            currentModel,
            receiptImages,
            accounts
          );

          // Успішно обробили
          if (isRetry) {
            // Фіксуємо деградацію: наступний чек теж починатиметься з цієї моделі
            _demoteModel(modelIdx);
            result.retryInfo = {
              modelUsed: currentModel,
              wasRetry: true,
              originalModel: selectedModel,
              failureReason: lastFailureReason || undefined
            };
          } else if (attempt > 0) {
            result.retryInfo = {
              modelUsed: currentModel,
              wasRetry: false
            };
          }

          return result;
        } catch (error) {
          lastError = error as Error;
          lastFailureReason = (error as Error).message;

          // Якщо це критична помилка (невалідний ключ), не робимо retry
          if (lastFailureReason.includes('INVALID_API_KEY')) {
            throw error;
          }

          // Якщо це остання спроба на цій моделі, переходимо до наступної
          if (attempt < MAX_RETRIES_PER_MODEL) {
            continue; // Спробуємо ще раз з тією ж моделлю
          }
          // Всі спроби на цій моделі вичерпані — деградуємо до наступної
          _demoteModel(modelIdx + 1);
        }
      }
    }

    // Якщо дійшли сюди, всі моделі вичерпалися
    throw lastError || new Error('Не вдалося обробити чек жодною моделлю');
  },

  /**
   * Допоміжна функція для виконання одного запиту з конкретною моделлю
   */
  async _extractWithModel(
    receiptText: string, 
    apiKey?: string,
    categories?: CategoryInfo[],
    receiptImage?: ReceiptImageInput,
    model?: string,
    receiptImages?: ReceiptImageInput[],
    accounts?: AccountInfo[]
  ): Promise<GeminiExtractResponse> {
    try {
      // Отримуємо API ключ
      const geminiApiKey = apiKey || await getGeminiApiKey();
      
      if (!geminiApiKey) {
        throw new Error('Gemini API ключ не налаштовано. Будь ласка, введіть API ключ в налаштуваннях.');
      }

      // Формуємо список категорій для промпту та мапу назва -> ID
      let categoriesPrompt = '';
      const categoryNameToIdMap = new Map<string, string>();
      
      if (categories && categories.length > 0) {
        const categoriesList = categories.map(cat => {
          categoryNameToIdMap.set(cat.name.toLowerCase().trim(), cat.id);
          return `- ${cat.name}`;
        }).join('\n');
        categoriesPrompt = `\n\nДоступні категорії в системі:\n${categoriesList}\n\nДля кожного товару спробуй призначити найбільш підходящу категорію зі списку вище на основі назви товару. Використовуй ТОЧНУ назву категорії зі списку вище. Якщо жодна категорія не підходить, залиш поле "categoryName" порожнім або не включай його.`;
      }

      const accountNameToIdMap = new Map<string, string>();
      let accountsPrompt = '';
      if (accounts && accounts.length > 0) {
        accounts.forEach(acc => accountNameToIdMap.set(acc.name.toLowerCase().trim(), acc.id));
        accountsPrompt = `\n\nДоступні рахунки:\n${accounts.map(acc => `- ${acc.name}`).join('\n')}`;
      }

      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      const normalizedReceiptText = receiptText?.trim() || '(Текст не надано, орієнтуйся на фото чека)';

      const prompt = `Ти — експерт з парсингу фінансових документів. Твоє завдання: проаналізувати текст чи фото фіскальних чеків та витягнути список товарів, їхні ціни та категорії.

${categoriesPrompt}${accountsPrompt}

Сьогоднішня дата: ${todayStr}

Текст чека:
"""
${normalizedReceiptText}
"""

### ПРАВИЛА ОБРОБКИ ТОВАРІВ ТА ЦІН:
1. Очищення назв: Виправляй відсутні пробіли та розшифровуй очевидні скорочення. Якщо назва довша за 100 символів або містить зайве сміття — скороти її, зберігши головну суть товару. Якщо ти не впевнений у назві (ймовірність < 90%) — залишай оригінальну.
2. Логіка знижок:
   - Якщо знижка вказана для конкретної позиції: відніми її від ціни цього товару. НЕ додавай таку знижку як окремий пункт.
   - Якщо в чеку є загальна знижка на весь чек (яка не розбита по товарах): додай її як окремий товар з від'ємною ціною (наприклад, "Загальна знижка").
3. Баланс чека (Важливо!): Загальна сума всіх витягнутих позицій повинна математично сходитися з підсумковою сумою (Total) у чеку. Якщо сума не сходиться, перевір, чи не пропустив ти знижку, ПДВ або пакет. У разі потреби додай нерозподілену різницю (наприклад, ПДВ) як окрему позицію.
4. Загальні поля: Ігноруй інформаційні рядки типу "Сума без ПДВ", номери кас, адреси тощо (якщо вони не потрібні для балансу).

### ПРАВИЛА КАТЕГОРИЗАЦІЇ:
${categories && categories.length > 0 ? `1. Призначай категорію кожному товару, використовуючи ТІЛЬКИ точні назви з наданого вище списку.
2. Аналізуй контекст: наприклад, якщо товар медичний, але вказано, що він для тварин, обирай категорію для тварин.
3. Якщо ти не впевнений у категорії (ймовірність < 80%) або жодна не підходить — не додавай поле "categoryName" для цього товару.` : 'Категоризація вимкнена.'}

### ДАТА ТА РАХУНОК:
1. Дата: якщо користувач у тексті явно вказав дату чека (наприклад "дата 12.05", "вчора") — використай її; інакше візьми дату з самого чека. Якщо року немає — використовуй поточний рік із сьогоднішньої дати. Якщо дату визначити неможливо — не додавай поле "receiptDate". Формат: YYYY-MM-DD.
2. Рахунок: ${accounts && accounts.length > 0 ? 'тільки якщо користувач у тексті явно вказав рахунок/картку, обери відповідну назву ЗІ СПИСКУ доступних рахунків (точна назва) у полі "accountName". Не вгадуй рахунок за даними чека; якщо не вказано або немає збігу — не додавай поле.' : 'не визначай.'}

### КІЛЬКІСТЬ ЧЕКІВ:
Якщо в тексті/на фото є КІЛЬКА окремих чеків (різні магазини, різні дати/часи, кілька підсумкових сум, або користувач явно пише про кілька чеків) — поверни КОЖЕН чек окремим об'єктом у масиві. Товари різних чеків не змішуй. Якщо чек один (один чек може бути на кількох фото) — масив має містити рівно один об'єкт. Правила вище застосовуй до кожного чека окремо.

### ФОРМАТ ВІДПОВІДІ:
Поверни результат ТІЛЬКИ у форматі валідного JSON без жодного додаткового тексту, форматування чи Markdown-блоків (без \`\`\`json).

Структура JSON — масив чеків:
[
  {
    "receiptName": "Назва магазину або закладу",
    "receiptDate": "2025-01-31",${accounts && accounts.length > 0 ? '\n    "accountName": "Назва рахунку зі списку",' : ''}
    "items": [
      {
        "name": "Зрозуміла назва товару",
        "price": 25.50${categories && categories.length > 0 ? ',\n        "categoryName": "Назва категорії зі списку"' : ''}
      }
    ]
  }
]`;
      

      const parts: Array<{ text: string } | { inlineData: { mimeType: string; data: string } }> = [];

      // Support multiple images (for single-receipt mode with multiple photos)
      const imagesToAdd = receiptImages?.length ? receiptImages : (receiptImage ? [receiptImage] : []);
      for (const img of imagesToAdd) {
        if (img.base64Data && img.mimeType) {
          parts.push({ inlineData: { mimeType: img.mimeType, data: img.base64Data } });
        }
      }

      parts.push({ text: prompt });

      const selectedModel = model || DEFAULT_GEMINI_MODEL;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      let response: Response;
      try {
        response = await fetch(`${getGeminiApiUrl(model!)}?key=${geminiApiKey}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({
            contents: [{
              parts
            }]
          })
        });
      } finally {
        clearTimeout(timeoutId);
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `GEMINI API error: ${response.status}`;
        
        // Спробуємо парсити JSON помилку
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error) {
            const geminiError = errorJson.error;
            errorMessage = geminiError.message || errorMessage;
            
            // Перевіряємо чи це помилка перевищення квоти
            if (response.status === 429 || geminiError.code === 429 || 
                geminiError.status === 'RESOURCE_EXHAUSTED' ||
                errorMessage.includes('quota') || 
                errorMessage.includes('exceeded')) {
              throw new Error('QUOTA_EXCEEDED');
            }
            
            // Перевіряємо чи це помилка перевантаження
            if (response.status === 503 || geminiError.code === 503 || 
                geminiError.status === 'UNAVAILABLE' || 
                errorMessage.includes('overloaded') || 
                errorMessage.includes('try again')) {
              throw new Error('MODEL_OVERLOADED');
            }
            
            // Перевіряємо чи це помилка невалідного API ключа
            if (response.status === 401 || response.status === 403 ||
                geminiError.code === 401 || geminiError.code === 403 ||
                errorMessage.toLowerCase().includes('invalid api key') ||
                errorMessage.toLowerCase().includes('api key not valid') ||
                errorMessage.toLowerCase().includes('authentication') ||
                errorMessage.toLowerCase().includes('unauthorized') ||
                errorMessage.toLowerCase().includes('permission denied') ||
                geminiError.status === 'UNAUTHENTICATED' ||
                geminiError.status === 'PERMISSION_DENIED') {
              throw new Error('INVALID_API_KEY');
            }
          }
        } catch (parseError) {
          // Якщо не вдалося парсити JSON, використовуємо оригінальний текст
          if (response.status === 429 || errorText.includes('quota') || errorText.includes('exceeded')) {
            throw new Error('QUOTA_EXCEEDED');
          }
          if (errorText.includes('overloaded') || errorText.includes('try again')) {
            throw new Error('MODEL_OVERLOADED');
          }
          if (response.status === 401 || response.status === 403 ||
              errorText.toLowerCase().includes('invalid api key') ||
              errorText.toLowerCase().includes('authentication') ||
              errorText.toLowerCase().includes('unauthorized')) {
            throw new Error('INVALID_API_KEY');
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Витягуємо текст з відповіді GEMINI
      const responseText = data.candidates?.[0]?.content?.parts?.[0]?.text;
      
      if (!responseText) {
        throw new Error('GEMINI API не повернув текст відповіді');
      }

      // Очищаємо текст від markdown кодових блоків якщо вони є
      let cleanedText = responseText.trim();
      if (cleanedText.startsWith('```json')) {
        cleanedText = cleanedText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      } else if (cleanedText.startsWith('```')) {
        cleanedText = cleanedText.replace(/```\n?/g, '');
      }

      // Парсимо JSON
      const parsedData: any = JSON.parse(cleanedText);
      
      // Нормалізуємо відповідь до масиву сирих чеків (підтримуємо також старі формати)
      let rawReceipts: any[];

      if (Array.isArray(parsedData)) {
        const isReceiptArray = parsedData.length > 0 && parsedData.every(r => r && typeof r === 'object' && Array.isArray(r.items));
        // Старий формат — плоский масив товарів
        rawReceipts = isReceiptArray ? parsedData : [{ items: parsedData }];
      } else if (parsedData && typeof parsedData === 'object' && Array.isArray(parsedData.items)) {
        rawReceipts = [parsedData];
      } else {
        throw new Error('GEMINI API повернув некоректний формат');
      }

      // Фільтруємо та валідуємо товари
      const parseItems = (rawItems: any[]): ExtractedItem[] => rawItems
      .filter(item => item && typeof item.name === 'string' && typeof item.price === 'number')
      .map(item => {
        const trimmedName = item.name.trim();
        
        // Визначаємо чи це знижка (за назвою або від'ємною ціною)
        const isDiscount = trimmedName.toLowerCase().includes('знижка') || 
                          trimmedName.toLowerCase().includes('скидка') || 
                          trimmedName.toLowerCase().includes('discount') ||
                          item.price < 0;
        
        const result: ExtractedItem = {
          name: trimmedName,
          price: item.price // Зберігаємо оригінальне значення (може бути від'ємним для знижок)
        };
        
        // НЕ призначаємо категорію для знижок
        if (!isDiscount) {
          // Якщо AI повернув categoryName, знаходимо відповідний ID
          if (item.categoryName && categoryNameToIdMap.size > 0) {
            const categoryName = String(item.categoryName).trim().toLowerCase();
            const categoryId = categoryNameToIdMap.get(categoryName);
            if (categoryId) {
              result.categoryId = categoryId;
            }
          }
          
          // Також перевіряємо categoryId на випадок якщо AI повернув його напряму
          if (item.categoryId && categories && categories.length > 0) {
            const categoryExists = categories.some(cat => cat.id === item.categoryId);
            if (categoryExists) {
              result.categoryId = item.categoryId;
            }
          }
        }
        
        return result;
      })
      .filter(item => item.name.length > 0 && item.price !== 0); // Дозволяємо від'ємні числа, але не нуль

    const receipts: ExtractedReceipt[] = rawReceipts
      .map(raw => {
        const receipt: ExtractedReceipt = { items: parseItems(raw.items) };
        if (raw.receiptName) receipt.receiptName = String(raw.receiptName);
        if (typeof raw.receiptDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.receiptDate.trim())) {
          receipt.receiptDate = raw.receiptDate.trim();
        }
        if (typeof raw.accountName === 'string') {
          receipt.accountId = accountNameToIdMap.get(raw.accountName.toLowerCase().trim());
        }
        return receipt;
      })
      .filter(r => r.items.length > 0);

    return { receipts };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('REQUEST_TIMEOUT');
      }
      console.error('Помилка обробки чека через GEMINI:', error);
      throw error;
    }
  }
};

